'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { ReactNode, cloneElement, isValidElement, Children, ReactElement } from 'react'
import clsx from 'clsx'
import styles from './styles.module.scss'

interface BreadcrumbsProps {
  children?: ReactNode
  used?: string
}

export default function Breadcrumbs({ children, used }: BreadcrumbsProps) {
  const pathname = usePathname()

  const pathnames = pathname.split('/').filter((x) => x)

  return (
    <nav className={clsx(styles.breadcrumb, used && styles[used])} aria-label='Breadcrumb'>
      <ul className={styles.breadcrumbList}>
        <li className={styles.breadcrumbItem}>
          <Link className={styles.breadcrumbLink} href='/'>
            TOP
          </Link>
        </li>
        {children
          ? (() => {
              // Process children of li element to add classes to Link/a tags
              const processLiChildren = (children: ReactNode): ReactNode => {
                return Children.map(children, (linkChild) => {
                  if (isValidElement(linkChild)) {
                    // Check for both Link component and native 'a' tag
                    if (linkChild.type === Link || linkChild.type === 'a') {
                      return cloneElement(linkChild as ReactElement<any>, {
                        className:
                          `${(linkChild.props as any).className || ''} ${styles.breadcrumbLink}`.trim(),
                      })
                    }
                    // If the element has children, recursively process them
                    if ((linkChild.props as any).children) {
                      return cloneElement(linkChild as ReactElement<any>, {
                        ...(linkChild.props as any),
                        children: processLiChildren((linkChild.props as any).children),
                      })
                    }
                  }
                  return linkChild
                })
              }

              // Process li element and add classes
              const processLiElement = (child: ReactNode, index: number): ReactNode => {
                if (isValidElement(child) && child.type === 'li') {
                  return cloneElement(child as ReactElement<any>, {
                    key: (child.props as any).key || `breadcrumb-${index}`,
                    className:
                      `${(child.props as any).className || ''} ${styles.breadcrumbItem}`.trim(),
                    children: processLiChildren((child.props as any).children),
                  })
                }
                return child
              }

              // Flatten React.Fragment and process all children
              const flattenAndProcess = (node: ReactNode): ReactNode[] => {
                const result: ReactNode[] = []
                let index = 0

                Children.forEach(node, (child) => {
                  if (!isValidElement(child)) {
                    result.push(child)
                    return
                  }

                  // Handle React.Fragment
                  if (
                    child.type === React.Fragment ||
                    (child.type as any) === Symbol.for('react.fragment')
                  ) {
                    const fragmentChildren = flattenAndProcess((child.props as any).children)
                    fragmentChildren.forEach((fragmentChild, i) => {
                      result.push(processLiElement(fragmentChild, index + i))
                    })
                    index += fragmentChildren.length
                  } else {
                    result.push(processLiElement(child, index++))
                  }
                })

                return result
              }

              return flattenAndProcess(children)
            })()
          : pathnames.map((value, index) => {
              const href = '/' + pathnames.slice(0, index + 1).join('/')
              const isLast = index === pathnames.length - 1

              return (
                <li className={styles.breadcrumbItem} key={href}>
                  {!isLast ? (
                    <Link className={styles.breadcrumbLink} href={href}>
                      {decodeURIComponent(value)}
                    </Link>
                  ) : (
                    <span className={styles.breadcrumbCurrent}>{decodeURIComponent(value)}</span>
                  )}
                </li>
              )
            })}
      </ul>
    </nav>
  )
}
